import { Box, Typography } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download'
import VerifiedIcon from '@mui/icons-material/Verified'
import PageHero from "../shared/PageHero";
import Section from "../shared/Section";
import SectionTitle from "../shared/SectionTitle";
import TvButton from "../shared/TvButton";
import TechStack from "../shared/TechStack";
import { COLORS, SITE } from "../../constants";
import resumeData, { TimelineData } from "../../utils/resumeData";
import usePageMeta from "../../utils/usePageMeta";

function dates(item: TimelineData) {
    if (!item.startDate) return ''
    return `${item.startDate} – ${item.endDate ?? 'Present'}`
}

function ExperienceItem({ item }: { item: TimelineData }) {
    return (
        <Box component="article" sx={{ display: 'grid', gap: { xs: 0.5, md: 4 }, gridTemplateColumns: { xs: '1fr', md: '160px 1fr' }, py: 4, borderTop: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ color: COLORS.muted, fontWeight: 600, fontSize: '0.95rem', pt: { md: 0.5 } }}>{dates(item)}</Typography>
            <Box>
                <Typography variant="h3">{item.name}</Typography>
                {item.role && <Typography sx={{ fontWeight: 600, color: COLORS.accentOnLight }}>{item.role}</Typography>}
                <Typography sx={{ mt: 1, color: 'text.secondary' }}>{item.description}</Typography>
                {item.highlights && (
                    <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5, display: 'grid', gap: 0.75, color: 'text.secondary', '& li::marker': { color: COLORS.accentOnLight } }}>
                        {item.highlights.map((h) => <Typography component="li" key={h}>{h}</Typography>)}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

function Resume() {
    usePageMeta({
        title: 'Resume: Full-Stack Software Engineer & AWS Consultant',
        description: 'Resume for Thomas Viles: full-stack software engineer with React, TypeScript, Python and AWS serverless experience. AWS Certified Solutions Architect and Developer, U.S. Army veteran.',
        path: '/resume',
    })

    return (
        <>
            <PageHero
                eyebrow="Resume"
                title={resumeData.name}
                intro={<>{resumeData.position} · {resumeData.location}<br /><br />{resumeData.summary}</>}
                actions={
                    <>
                        <TvButton text="Download PDF" link={SITE.resumePdf} icon={<DownloadIcon />} />
                        <TvButton text="LinkedIn" link={SITE.linkedin} variant="outline" tone="dark" />
                    </>
                }
            />

            <Section>
                <SectionTitle eyebrow="Experience" text="Work history" />
                {resumeData.workHistory.map((item) => <ExperienceItem key={item.name} item={item} />)}
            </Section>

            <Section tone="light">
                <SectionTitle eyebrow="Credentials" text="Certifications & education" />
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    {resumeData.certifications.map((c) => (
                        <Box key={c.name} sx={{ p: 3, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`, display: 'flex', gap: 2 }}>
                            <VerifiedIcon sx={{ color: COLORS.accentOnLight, mt: 0.3 }} />
                            <Box>
                                <Typography variant="h4" component="h3">{c.name}</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>{c.description}</Typography>
                            </Box>
                        </Box>
                    ))}
                    {resumeData.education.map((e) => (
                        <Box key={e.name} sx={{ p: 3, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}>
                            <Typography variant="h4" component="h3">{e.name}</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{e.role} · {dates(e)}</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>{e.description}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>

            <Section>
                <SectionTitle eyebrow="Military service" text="Before software" />
                {resumeData.otherExperience.map((item) => <ExperienceItem key={item.name} item={item} />)}
            </Section>

            <Section tone="light" tight>
                <SectionTitle eyebrow="Technical background" text="Skills" />
                <TechStack />
            </Section>
        </>
    );
}

export default Resume;
